import { useMemo } from "react";
import { Link } from "react-router-dom";
import { LuClock } from "react-icons/lu";
import { RateBoardItem, useRateBoardQuery } from "../../Redux/Api/RateBoard";
import { useCiT } from "../../common/ci/i18n";
import { inclGst } from "../../utils/priceText";

/**
 * Peeli "Aaj ka rate" patti — rulebook §1 rule 1: rate sabse upar, har page par,
 * login ke bina. Data /global/rate-board se; endpoint fail ya khaali ho to patti
 * dikhti hi nahi (koi khaali box nahi).
 */
const fmtDate = (iso?: string): string => {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

/**
 * Backend itemName "12MM INDOSTAR · INDOSTAR" jaisa aata hai (item · company).
 * Patti me: category ka pehla shabd + item + company (sirf tab jab naam me na ho) —
 * "TMT 12MM INDOSTAR", "CEMENT WONDER PPC", "ANGLE 35X35X5MM KVS".
 */
export const shortName = (it: RateBoardItem): string => {
  const raw = String(it.itemName || "").trim();
  const base = raw.split(" · ")[0].trim();
  const company = String(it.companyName || "").trim();
  const cat = String(it.categoryName || "").trim().split(/\s+/)[0] || "";
  const up = base.toUpperCase();
  const parts = [
    cat && !up.includes(cat.toUpperCase()) ? cat : "",
    base,
    company && !up.includes(company.toUpperCase()) ? company : "",
  ].filter(Boolean);
  const n = parts.join(" ");
  return n.length > 30 ? `${n.slice(0, 29)}…` : n;
};

const RateBand = ({ compact = false }: { compact?: boolean }) => {
  const { data, isError } = useRateBoardQuery();
  const t = useCiT();
  const items = useMemo(
    () =>
      ((data && data.items) || [])
        .filter((it: RateBoardItem) => Number(it.rate) > 0)
        .slice(0, compact ? 4 : 6),
    [data, compact]
  );

  if (isError || !items.length) return null;

  return (
    <div className="w-full border-b border-[#e3ba2c] bg-ci-band text-[#3a2f00]">
      <div
        className={`mx-auto flex max-w-[1440px] items-center gap-5 overflow-x-auto whitespace-nowrap px-4 no-scrollbar ${
          compact ? "h-[36px] text-[13px]" : "h-[40px] px-5 text-[14px]"
        } font-semibold`}
      >
        <span className="flex shrink-0 items-center gap-1.5 font-display text-[15px] uppercase">
          <LuClock size={15} /> {t("Aaj ka rate")} · {fmtDate(data?.updatedAt)}
        </span>
        {items.map((it: RateBoardItem) => (
          <Link
            key={it.companyProductId}
            to={`/product/${it.companyProductId}/${encodeURIComponent(it.itemName || "item")}`}
            className="shrink-0 hover:underline"
          >
            {shortName(it)}{" "}
            <b className="ci-num">
              ₹{inclGst(it.rate, it.gstPercent).toFixed(2)}
              {it.unit ? `/${it.unit}` : ""}
            </b>{" "}
            <span className="text-[#6b5a00]">incl. GST</span>
          </Link>
        ))}
        <Link to="/rate-today" className="ml-auto shrink-0 underline">
          {t("Full rate list")}
        </Link>
      </div>
    </div>
  );
};

export default RateBand;
